import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { requireAdmin } from "@/lib/admin-guard";
import { supabase } from "@/integrations/supabase/client";
import {
  adminDeleteShopProduct,
  adminListShopOrders,
  adminListShopProducts,
  adminSaveShopProduct,
} from "@/lib/shop-admin.functions";
import { ShopCategoriesAdmin, useAdminCategories } from "@/components/shop/ShopCategoriesAdmin";
import { SHOP_NEEDS } from "@/lib/shop-pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { adminGiftAction, adminListGifts } from "@/lib/gift.functions";

export const Route = createFileRoute("/admin_/shop")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Shop — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminShopPage,
});

type Product = {
  id?: string;
  slug: string;
  title: string;
  summary: string | null;
  description: string | null;
  price_cents: number;
  currency: string;
  image_url: string | null;
  download_path: string | null;
  is_published: boolean;
  sort_order: number;
  product_type: "digital" | "affiliate";
  category_slug: string | null;
  need_tags: string[];
  affiliate_url: string | null;
  details: string | null;
  is_illustration: boolean;
  is_featured: boolean;
  is_recommended: boolean;
  sale_price_cents: number | null;
  sale_ends_at: string | null;
  affiliate_clicks?: number;
};

type Order = {
  id: string;
  email: string | null;
  product_title: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  created_at: string;
};

const EMPTY: Product = {
  slug: "",
  title: "",
  summary: "",
  description: "",
  price_cents: 900,
  currency: "gbp",
  image_url: "",
  download_path: "",
  is_published: false,
  sort_order: 0,
  product_type: "affiliate",
  category_slug: null,
  need_tags: [],
  affiliate_url: "",
  details: "",
  is_illustration: false,
  is_featured: false,
  is_recommended: false,
  sale_price_cents: null,
  sale_ends_at: null,
};

function money(cents: number, currency: string) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function AdminShopPage() {
  const listProducts = useServerFn(adminListShopProducts);
  const saveProduct = useServerFn(adminSaveShopProduct);
  const deleteProduct = useServerFn(adminDeleteShopProduct);
  const listOrders = useServerFn(adminListShopOrders);

  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"image" | "file" | null>(null);
  const { categories, reload: reloadCategories } = useAdminCategories();

  async function load() {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([listProducts(), listOrders()]);
      setProducts((p.products ?? []) as unknown as Product[]);
      setOrders((o.orders ?? []) as unknown as Order[]);
    } catch {
      toast.error("Couldn't load the shop");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function upload(kind: "image" | "file", file: File) {
    setUploading(kind);
    try {
      const path = `${kind === "image" ? "images" : "files"}/${crypto.randomUUID()}-${file.name.replace(/[^\w.-]/g, "_")}`;
      const { error } = await supabase.storage.from("shop-downloads").upload(path, file);
      if (error) throw new Error(error.message);
      setDraft((d) =>
        d ? { ...d, ...(kind === "image" ? { image_url: path } : { download_path: path }) } : d,
      );
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(null);
    }
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      await saveProduct({
        data: {
          ...(draft.id ? { id: draft.id } : {}),
          slug: draft.slug.trim(),
          title: draft.title.trim(),
          summary: draft.summary || null,
          description: draft.description || null,
          price_cents: Math.round(draft.price_cents),
          currency: draft.currency || "gbp",
          image_url: draft.image_url || null,
          download_path: draft.download_path || null,
          is_published: draft.is_published,
          sort_order: draft.sort_order,
          product_type: draft.product_type,
          category_slug: draft.category_slug || null,
          need_tags: draft.need_tags ?? [],
          affiliate_url: draft.product_type === "affiliate" ? draft.affiliate_url || null : null,
          details: draft.details || null,
          is_illustration: draft.is_illustration,
          is_featured: draft.is_featured,
          is_recommended: draft.is_recommended,
          sale_price_cents: draft.product_type === "digital" && draft.sale_price_cents ? Math.round(draft.sale_price_cents) : null,
          sale_ends_at: draft.product_type === "digital" && draft.sale_price_cents && draft.sale_ends_at ? new Date(draft.sale_ends_at).toISOString() : null,
        },
      });
      toast.success("Saved");
      setDraft(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to admin
      </Link>

      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Shop</h1>
        <Button onClick={() => setDraft({ ...EMPTY })}>
          <Plus className="me-2 h-4 w-4" aria-hidden="true" /> New item
        </Button>
      </div>

      {draft ? (
        <section className="mb-8 space-y-4 rounded-2xl border bg-card p-5">
          <h2 className="font-medium">{draft.id ? "Edit item" : "New item"}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ptype">Type</Label>
              <select id="ptype" className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={draft.product_type}
                onChange={(e) => setDraft({ ...draft, product_type: e.target.value as Product["product_type"] })}>
                <option value="affiliate">Amazon product (affiliate link)</option>
                <option value="digital">Our digital download (sold here)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="pcat">Category</Label>
              <select id="pcat" className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={draft.category_slug ?? ""}
                onChange={(e) => setDraft({ ...draft, category_slug: e.target.value || null })}>
                <option value="">— none —</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>
          {draft.product_type === "affiliate" && (
            <div>
              <Label htmlFor="aff">Amazon link</Label>
              <Input id="aff" placeholder="https://…" value={draft.affiliate_url ?? ""} onChange={(e) => setDraft({ ...draft, affiliate_url: e.target.value })} />
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="slug">Web address (lowercase, dashes)</Label>
              <Input
                id="slug"
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="price">{draft.product_type === "affiliate" ? "Price in pence (not shown for Amazon items)" : "Price in pence / cents"}</Label>
              <Input
                id="price"
                type="number"
                value={draft.price_cents}
                onChange={(e) => setDraft({ ...draft, price_cents: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="summary">Short line</Label>
            <Input
              id="summary"
              value={draft.summary ?? ""}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={5}
              value={draft.description ?? ""}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="details">Details / specifications (one per line)</Label>
            <Textarea id="details" rows={4} value={draft.details ?? ""} onChange={(e) => setDraft({ ...draft, details: e.target.value })} />
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">Helps with</legend>
            <div className="flex flex-wrap gap-3">
              {SHOP_NEEDS.map((n) => (
                <label key={n.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={draft.need_tags.includes(n.id)}
                    onChange={(e) => setDraft({ ...draft, need_tags: e.target.checked ? [...draft.need_tags, n.id] : draft.need_tags.filter((t) => t !== n.id) })} />
                  {n.label}
                </label>
              ))}
            </div>
          </fieldset>
          {draft.product_type === "digital" && (
            <div className="grid gap-4 rounded-xl border p-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="sale">Offer price in pence / cents (optional)</Label>
                <Input id="sale" type="number" value={draft.sale_price_cents ?? ""} onChange={(e) => setDraft({ ...draft, sale_price_cents: e.target.value ? Number(e.target.value) : null })} />
              </div>
              <div>
                <Label htmlFor="saleEnd">Offer ends</Label>
                <Input id="saleEnd" type="datetime-local" value={draft.sale_ends_at ? draft.sale_ends_at.slice(0, 16) : ""} onChange={(e) => setDraft({ ...draft, sale_ends_at: e.target.value || null })} />
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="image">Preview picture</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void upload("image", f);
                }}
              />
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {uploading === "image" ? "Uploading…" : draft.image_url || "No picture yet"}
              </p>
            </div>
            <div>
              <Label htmlFor="file">Download file</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void upload("file", f);
                }}
              />
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {uploading === "file" ? "Uploading…" : draft.download_path || "No file yet"}
              </p>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.is_published}
              onChange={(e) => setDraft({ ...draft, is_published: e.target.checked })}
            />
            Show this item in the shop
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.is_featured} onChange={(e) => setDraft({ ...draft, is_featured: e.target.checked })} />
            Featured
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.is_recommended} onChange={(e) => setDraft({ ...draft, is_recommended: e.target.checked })} />
            Recommended
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.is_illustration} onChange={(e) => setDraft({ ...draft, is_illustration: e.target.checked })} />
            This picture is an illustration, not an official product photo
          </label>

          <div className="flex gap-3">
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Save
            </Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </section>
      ) : null}

      <ShopCategoriesAdmin categories={categories} onChange={reloadCategories} />

      <section className="mb-10">
        <h2 className="mb-3 font-medium">Items</h2>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />
        ) : products.length === 0 ? (
          <p className="text-muted-foreground">No items yet.</p>
        ) : (
          <ul className="space-y-2">
            {products.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4"
              >
                <div>
                  <p className="font-medium">
                    {p.title}{" "}
                    <span className="text-xs text-muted-foreground">
                      {p.is_published ? "· live" : "· hidden"}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    /{p.slug} · {p.product_type === "affiliate" ? `Amazon · ${p.affiliate_clicks ?? 0} taps` : money(p.price_cents, p.currency)}{p.is_featured ? " · featured" : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setDraft(p)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={`Delete ${p.title}`}
                    onClick={async () => {
                      if (!p.id) return;
                      await deleteProduct({ data: { id: p.id } });
                      toast.success("Deleted");
                      await load();
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <GiftOrders />

      <section>
        <h2 className="mb-3 font-medium">Orders</h2>
        {orders.length === 0 ? (
          <p className="text-muted-foreground">No orders yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-start">
                <tr>
                  <th className="p-3">Date</th>
                  <th className="p-3">Item</th>
                  <th className="p-3">Buyer</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t">
                    <td className="p-3">{new Date(o.created_at).toLocaleDateString("en-GB")}</td>
                    <td className="p-3">{o.product_title ?? "—"}</td>
                    <td className="p-3">{o.email ?? "—"}</td>
                    <td className="p-3">{money(o.amount_cents, o.currency)}</td>
                    <td className="p-3">{o.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function GiftOrders() {
  const list = useServerFn(adminListGifts);
  const act = useServerFn(adminGiftAction);
  type GiftRow = { id: string; code: string | null; plan_months: number; amount_cents: number; buyer_email: string; recipient_email: string | null; delivery_mode: string; status: string; paid_at: string | null; voucher_sent_at: string | null; redeemed_at: string | null };
  const [gifts, setGifts] = useState<GiftRow[]>([]);
  const load = () => list().then((r) => setGifts(r.gifts as GiftRow[])).catch(() => toast.error("Couldn't load gift orders"));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function run(id: string, action: "resend" | "cancel") {
    if (action === "cancel" && !confirm("Cancel this gift? The code will stop working.")) return;
    const r = await act({ data: { id, action } });
    if ("error" in r && r.error) toast.error(r.error);
    else toast.success(action === "resend" ? "Voucher email resent" : "Gift cancelled");
    load();
  }
  const d = (x: string | null) => (x ? new Date(x).toLocaleDateString("en-GB") : "—");
  return (
    <section className="mb-10">
      <h2 className="mb-3 font-medium">Gift orders</h2>
      {gifts.length === 0 ? (
        <p className="text-muted-foreground">No gift orders yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-start">
              <tr>
                <th className="p-3">Paid</th><th className="p-3">Plan</th><th className="p-3">Buyer</th><th className="p-3">Sent to</th>
                <th className="p-3">Code</th><th className="p-3">Status</th><th className="p-3">Redeemed</th><th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {gifts.map((g) => (
                <tr key={g.id} className="border-t">
                  <td className="p-3">{d(g.paid_at)}</td>
                  <td className="p-3">{g.plan_months} mo · ${(g.amount_cents / 100).toFixed(2)}</td>
                  <td className="p-3">{g.buyer_email}</td>
                  <td className="p-3">{g.delivery_mode === "gift" ? g.recipient_email : "Buyer (self)"}{g.voucher_sent_at ? " ✓" : ""}</td>
                  <td className="p-3 font-mono text-xs">{g.code}</td>
                  <td className="p-3">{g.status}</td>
                  <td className="p-3">{d(g.redeemed_at)}</td>
                  <td className="p-3 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => run(g.id, "resend")}>Resend</Button>{" "}
                    {g.status === "paid" ? <Button size="sm" variant="ghost" onClick={() => run(g.id, "cancel")}>Cancel</Button> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
